import React, { useMemo } from 'react';
import { Row, FlexboxGrid, Divider } from 'rsuite';
import { useSelector } from 'react-redux';
import {
  selectConfigExpressTax,
  selectConfigExpressAllTaxCollectionAgreementWith,
} from '../../../store/slices/config/configSelectors';
import { TableComponent, SummaryRow } from '../../common/TableComponent';
import { getDateRelatedBucket } from '../../../common/utils';
import { CollectionAgreementBucket, TaxableActivity } from '../../../types/tax';
import { TaxBreakdownProvider } from './TaxBreakdownProvider';
import { TaxBreakdownItem } from './TaxBreakdownItem';

function getSummaryDataFromLicense(
  license: TaxableActivity[],
  periodLabel: string,
  buckets: CollectionAgreementBucket[]
): SummaryRow[] {
  const summaryRows: SummaryRow[] = license.map((provider) => {
    const totDueSum = Object.values(provider.taxTypes ?? {}).reduce(
      (sum, taxType) => sum + (taxType.total ?? 0),
      0
    );

    const bucket = buckets.find((b) => b.key === provider.key);
    const label = bucket?.label
      ? `Tax Due for ${bucket.label}`
      : `Tax Due for ${provider.key}`;
    return {
      label,
      value: `$${totDueSum.toFixed(2)}`,
    };
  });

  const total = summaryRows.reduce(
    (sum, row) => sum + parseFloat(row.value.replace('$', '')),
    0
  );

  summaryRows.push({
    label: `Total Due for ${periodLabel}`,
    value: `$${total.toFixed(2)}`,
    bold: true,
  });

  return summaryRows;
}

interface TaxPayableBreakdownTableProps {
  period?: { startDate?: string };
  license: TaxableActivity[];
  periodLabel: string;
  processingFee?: number;
  totalPayment?: number | string;
}

const TaxPayableBreakdownTable = ({
  period,
  license,
  periodLabel,
  processingFee,
  totalPayment,
}: TaxPayableBreakdownTableProps) => {
  const allBuckets = useSelector(
    selectConfigExpressAllTaxCollectionAgreementWith
  );
  const buckets = useMemo(() => {
    return getDateRelatedBucket(allBuckets, period?.startDate ?? '');
  }, [allBuckets, period]);

  const taxConfiguration = useSelector(selectConfigExpressTax);
  const summaryData = useMemo(() => {
    return getSummaryDataFromLicense(license, periodLabel, buckets);
  }, [license, periodLabel, buckets]);

  return (
    <>
      <FlexboxGrid
        as={Row}
        align="bottom"
      >
        <FlexboxGrid.Item colspan={24}>
          <TableComponent
            data={summaryData}
            title="Summary"
          />
        </FlexboxGrid.Item>
        <FlexboxGrid.Item colspan={24}>
          <Divider />
        </FlexboxGrid.Item>
      </FlexboxGrid>
      <FlexboxGrid as={Row}>
        {license.map((provider) => {
          const bucket = buckets.find((b) => b.key === provider.key);
          if (!bucket) return <></>;
          return (
            <FlexboxGrid.Item
              key={provider.key}
              as={Row}
              colspan={24}
            >
              <TaxBreakdownProvider
                taxableActivity={provider}
                configuration={taxConfiguration}
                bucket={bucket}
              />
            </FlexboxGrid.Item>
          );
        })}
      </FlexboxGrid>
      {(processingFee !== undefined || totalPayment !== undefined) && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {processingFee !== undefined && (
              <TaxBreakdownItem
                title="Processing Fee"
                value={processingFee}
              />
            )}
            {totalPayment !== undefined && (
              <TaxBreakdownItem
                title={`Total Payment Due ${periodLabel}`}
                value={totalPayment}
                bold
              />
            )}
          </tbody>
        </table>
      )}
    </>
  );
};

export default TaxPayableBreakdownTable;
